const registries = new WeakMap();
let registrySequence = 0;

export class MapLegendRegistry {
    constructor(map, documentRef = document) {
        this.map = map;
        this.document = documentRef;
        this.entries = new Map();
        this.listeners = new Set();
        this.controlAdded = false;
        this.opened = false;
        this.previousFocus = null;
        const instanceId = 'ms-map-legends-' + (++registrySequence);
        this.root = documentRef.createElement('div');
        this.root.className = 'maplibregl-ctrl ms-map-legends-control';
        this.root.setAttribute('data-map-legends', '');
        this.portal = documentRef.createElement('div');
        this.portal.className = 'ms-map-legends-portal';
        this.trigger = documentRef.createElement('button');
        this.trigger.type = 'button';
        this.trigger.className = 'ms-button ms-button--secondary ms-button--sm ms-button--pill ms-map-legends-trigger';
        this.trigger.setAttribute('aria-controls', instanceId);
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="m2 12 10 5 10-5"/><path d="m2 17 10 5 10-5"/></svg><span>Legenda</span>';
        this.count = documentRef.createElement('span');
        this.count.className = 'ms-map-legends-trigger__count';
        this.trigger.appendChild(this.count);

        this.drawer = documentRef.createElement('aside');
        this.drawer.id = instanceId;
        this.drawer.className = 'ms-map-legends-drawer';
        this.drawer.setAttribute('role', 'dialog');
        this.drawer.setAttribute('aria-modal', 'false');
        this.drawer.setAttribute('aria-labelledby', instanceId + '-title');
        this.drawer.setAttribute('aria-hidden', 'true');
        const header = documentRef.createElement('header');
        header.className = 'ms-map-legends-drawer__head';
        const heading = documentRef.createElement('h2');
        heading.id = instanceId + '-title';
        heading.textContent = 'Legenda mapy';
        this.closeButton = documentRef.createElement('button');
        this.closeButton.type = 'button';
        this.closeButton.className = 'ms-button ms-button--icon ms-map-legends-drawer__close';
        this.closeButton.setAttribute('aria-label', 'Zamknij legendę mapy');
        this.closeButton.textContent = '×';
        header.append(heading, this.closeButton);
        this.body = documentRef.createElement('div');
        this.body.className = 'ms-map-legends-drawer__body';
        this.drawer.append(header, this.body);
        this.root.appendChild(this.trigger);
        this.portal.appendChild(this.drawer);
        const portalHost = documentRef.body || documentRef.documentElement;
        if (portalHost) portalHost.appendChild(this.portal);

        this.trigger.addEventListener('click', event => {
            event.stopPropagation();
            this.open();
        });
        this.closeButton.addEventListener('click', () => this.close());
        this.keydownHandler = event => this.handleKeydown(event);
        documentRef.addEventListener('keydown', this.keydownHandler);
        this.control = {
            onAdd: () => this.root,
            onRemove: () => {
                this.close(false);
                this.root.remove();
                this.controlAdded = false;
            },
        };
    }

    show(key, owner, options = {}) {
        if (!key || !owner) throw new Error('Legenda mapy wymaga klucza i właściciela.');
        let entry = this.entries.get(key);
        if (!entry) {
            const element = this.document.createElement('section');
            element.setAttribute('data-map-legend-key', key);
            entry = { element, owners: new Set() };
            this.entries.set(key, entry);
            this.body.appendChild(element);
        }

        entry.owners.add(owner);
        entry.element.className = 'ms-map-legend'
            + (options.className ? ' ' + options.className : '');
        if (options.ariaLabel) entry.element.setAttribute('aria-label', options.ariaLabel);
        if (options.role) entry.element.setAttribute('role', options.role);
        if (typeof options.render === 'function') {
            entry.element.replaceChildren();
            options.render(entry.element);
        }
        this.updateCount();
        this.ensureControl();
        return entry.element;
    }

    hide(key, owner) {
        const entry = this.entries.get(key);
        if (!entry) return;
        entry.owners.delete(owner);
        if (entry.owners.size > 0) return;
        entry.element.remove();
        this.entries.delete(key);
        this.updateCount();
        if (this.entries.size === 0 && this.controlAdded && this.map.removeControl) {
            this.map.removeControl(this.control);
        }
    }

    setVisible(key, owner, visible, options = {}) {
        return visible ? this.show(key, owner, options) : this.hide(key, owner);
    }

    ensureControl() {
        if (this.controlAdded || !this.map || !this.map.addControl) return;
        this.controlAdded = true;
        this.map.addControl(this.control, 'bottom-left');
    }

    updateCount() {
        const count = this.entries.size;
        this.count.textContent = String(count);
        this.trigger.setAttribute('aria-label', 'Otwórz legendę mapy. Aktywne sekcje: ' + count + '.');
        this.notify();
    }

    getState() {
        return { opened: this.opened, count: this.entries.size };
    }

    subscribe(listener) {
        if (typeof listener !== 'function') return () => {};
        this.listeners.add(listener);
        listener(this.getState());
        return () => this.listeners.delete(listener);
    }

    notify() {
        const state = this.getState();
        this.listeners.forEach(listener => listener(state));
    }

    open() {
        if (this.opened || this.entries.size === 0) return;
        this.opened = true;
        this.previousFocus = this.document.activeElement || null;
        this.portal.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.drawer.setAttribute('aria-hidden', 'false');
        this.notify();
    }

    close(restoreFocus = true) {
        if (!this.opened) return;
        this.opened = false;
        this.portal.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.drawer.setAttribute('aria-hidden', 'true');
        if (restoreFocus && this.previousFocus && this.previousFocus.focus) this.previousFocus.focus();
        this.previousFocus = null;
        this.notify();
    }

    handleKeydown(event) {
        if (!this.opened) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
        }
    }

    ownerCount(key) {
        const entry = this.entries.get(key);
        return entry ? entry.owners.size : 0;
    }
}

export function getMapLegendRegistry(map) {
    if (!map) return null;
    let registry = registries.get(map);
    if (!registry) {
        registry = new MapLegendRegistry(map);
        registries.set(map, registry);
    }
    return registry;
}

export function renderMapLegend(element, definition) {
    const documentRef = element.ownerDocument || document;
    const title = documentRef.createElement('strong');
    title.className = 'ms-map-legend__title';
    title.textContent = definition.title;
    element.appendChild(title);

    const items = documentRef.createElement('div');
    items.className = 'ms-map-legend__grid' + (definition.singleColumn ? ' ms-map-legend__grid--single-column' : '');
    (definition.items || []).forEach(function(item) {
        const row = documentRef.createElement('span');
        row.className = 'ms-map-legend__item';
        const swatch = documentRef.createElement('i');
        swatch.className = 'ms-map-legend__swatch ms-map-legend__swatch--' + (item.shape || 'area');
        if (item.color) swatch.style.setProperty('--legend-color', item.color);
        row.append(swatch, documentRef.createTextNode(item.label));
        items.appendChild(row);
    });
    element.appendChild(items);

    if (definition.note) {
        const note = documentRef.createElement('small');
        note.textContent = definition.note;
        element.appendChild(note);
    }

    if (definition.source) {
        const link = documentRef.createElement('a');
        link.className = 'ms-link ms-map-legend__source';
        link.setAttribute('href', definition.source.href);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        link.setAttribute('aria-label', definition.source.label + ' (otwiera nową kartę)');
        link.textContent = definition.source.label;
        element.appendChild(link);
    }
}
